import type { ComputedRef } from 'vue'

import { useSubscriptionsStore, useSubscriptionsApi } from '@widgets/subscriptions'
import { useOnTimezoneChange } from '@entities/viewer'
import { SubscriptionModal } from '@entities/subscription'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

const SUBSCRIPTION_HASH = /^#(\d+)$/

interface CustomerSubscriptionsPageReturn {
  /** The modal to mount — only while its subscription is still on the current page of results. */
  activeModal: ComputedRef<SubscriptionModal | null>
  isRetryOpen: ComputedRef<boolean>
}

/** The customer's Subscriptions tab: the Subscriptions page lifecycle, narrowed to one customer. */
export const useCustomerSubscriptionsPage = (): CustomerSubscriptionsPageReturn => {
  const route = useRoute()
  const store = useSubscriptionsStore()

  const pagePath = resolveUrl(AppRoute.CustomersSubscriptions, { id: route.params.id as string })

  const activeModal = computed<SubscriptionModal | null>(() => (store.getModalSubscription ? store.getActiveModal : null))
  const isRetryOpen = computed<boolean>(() => store.getRetryOrder !== null)

  /** `#{id}` (from the purchase cards and the discount modal) opens that subscription's details, as the legacy tab did. */
  const openHashedSubscription = (): void => {
    const subscriptionId = Number(SUBSCRIPTION_HASH.exec(route.hash)?.[1] ?? 0)

    if (subscriptionId && store.getSubscriptions.some(subscription => subscription.id === subscriptionId)) {
      store.openModal(SubscriptionModal.Details, subscriptionId)
    }
  }

  const load = async (): Promise<void> => {
    await useSubscriptionsApi()
  }

  onMounted(async () => {
    store.resetPageState()
    await load()
    openHashedSubscription()
  })

  // Filters and pagination navigate by query; refetching keeps the table in sync with the URL.
  watch(() => route.query, () => {
    if (route.path === pagePath) void load()
  })

  // Timestamps arrive pre-shifted into the admin's view timezone, so a timezone change needs a refetch.
  useOnTimezoneChange(() => {
    void load()
  })

  onBeforeUnmount(() => {
    store.resetPageState()
  })

  return {
    activeModal,
    isRetryOpen,
  }
}
